import type { FC } from 'react';
import Proptypes from 'prop-types';
import { Box } from '@material-ui/core';
import { Status } from '../status';
import { EJobResultType, EJobTitle, ETaskName, FlagType, Job, Task } from '../../types/job';
import { getJobRunningStatus, getResultStatus } from '../../utils/jobs-helper';
import { Flag as FlagIcon } from '../../icons/flag';

interface EventStatusProps {
  event: Job | Task;
  reverseOrder?: boolean;
}

interface StatusVariant {
  color: string;
  label: string;
}

const DEFAULT_STATUS_VARIANT: StatusVariant = {
  color: 'text.secondary',
  label: 'Unknown status',
};

const getVariant = (event: Job | Task): StatusVariant => {
  const result = getResultStatus(event);
  const isRunning = getJobRunningStatus(event);

  const title = 'title' in event ? event.title : event.taskName;
  switch (title) {
    default:
      return DEFAULT_STATUS_VARIANT;

    // Authentication
    case EJobTitle.PROTECTION:
    case ETaskName.PROTECTION: {
      if (isRunning) {
        return { color: 'primary.main', label: 'Protection in progress' };
      }
      if (result === EJobResultType.OK) {
        return { color: 'info.main', label: 'Protected' };
      }
      return { color: 'error.main', label: 'Unable To Protect' };
    }

    case EJobTitle.VERIFICATION:
    case ETaskName.VERIFICATION: {
      if (isRunning) {
        return { color: 'primary.main', label: 'Verification in progress' };
      }
      if (result === EJobResultType.OK) {
        return { color: 'success.main', label: 'Verified' };
      }
      if (result === EJobResultType.NOT_VERIFIED) {
        return { color: 'error.main', label: 'Not Verified' };
      }

      return { color: 'text.primary', label: 'Unable To Verify' };
    }

    // Identification
    case EJobTitle.REGISTRATION:
    case ETaskName.IDENTIFICATION_REGISTRATION: {
      if (isRunning) {
        return { color: 'primary.main', label: 'Item Registration in progress' };
      }
      if (result === EJobResultType.OK) {
        return { color: 'info.main', label: 'Item Registered' };
      }
      return { color: 'error.main', label: 'Unable To Register Item' };
    }

    case EJobTitle.IDENTIFICATION:
    case ETaskName.IDENTIFICATION_IDENTIFICATION: {
      if (isRunning) {
        return { color: 'primary.main', label: 'Identification in progress' };
      }
      if (result === EJobResultType.OK) {
        return { color: 'success.main', label: 'Identified' };
      }
      if (result === EJobResultType.NOT_VERIFIED) {
        return { color: 'error.main', label: 'Not registered item' };
      }
      return { color: 'error.main', label: 'Unable To Identify' };
    }

    case EJobTitle.DELETE_REGISTRATION:
    case ETaskName.IDENTIFICATION_DELETE_REGISTRATION: {
      if (isRunning) {
        return { color: 'primary.main', label: 'Delete registered item in progress' };
      }
      if (result === EJobResultType.OK) {
        return { color: 'warning.main', label: 'Deleted registred item' };
      }
      return { color: 'error.main', label: 'Unable To Delete registred item' };
    }

    // Classification
    case EJobTitle.CLASSIFICATION_REGISTRATION:
    case ETaskName.CLASSIFICATION_REGISTRATION: {
      if (isRunning) {
        return { color: 'primary.main', label: 'SKU registration in progress' };
      }
      if (result === EJobResultType.OK) {
        return { color: 'info.main', label: 'SKU Registered' };
      }
      return { color: 'error.main', label: 'Unable To Register SKU' };
    }

    case EJobTitle.CLASSIFICATION_CLASSIFICATION:
    case ETaskName.CLASSIFICATION_CLASSIFICATION: {
      if (isRunning) {
        return { color: 'primary.main', label: 'SKU Classification in progress' };
      }
      if (result === EJobResultType.OK) {
        return { color: 'success.main', label: 'SKU Classified' };
      }
      if (result === EJobResultType.NOT_VERIFIED) {
        return { color: 'error.main', label: 'Not classified SKU' };
      }
      return { color: 'error.main', label: 'Unable To Classify SKU' };
    }

    case EJobTitle.CLASSIFICATION_DELETE_REGISTRATION:
    case ETaskName.CLASSIFICATION_DELETE_REGISTRATION: {
      if (isRunning) {
        return { color: 'primary.main', label: 'Delete registered SKU in progress' };
      }
      if (result === EJobResultType.OK) {
        return { color: 'warning.main', label: 'Deleted registered SKU' };
      }
      return { color: 'error.main', label: 'Unable To Delete registered SKU' };
    }

    // Other
    case EJobTitle.GENERIC_LOG:
    case ETaskName.GENERIC_LOG: {
      return { color: 'text.secondary', label: 'Generic log' };
    }
    case EJobTitle.DIAGNOSTIC_LOG:
    case ETaskName.DIAGNOSTIC_LOG: {
      return { color: 'text.secondary', label: 'Diagnostic log' };
    }
    case EJobTitle.BATCH_AUTHENTICATION_VERIFICATION_SCAN:
    case ETaskName.BATCH_AUTHENTICATION_VERIFICATION_SCAN: {
      if (result === EJobResultType.OK) {
        return { color: 'success.main', label: 'Batch authentication verification scan' };
      }
      return { color: 'error.main', label: 'Batch authentication verification scan' };
    }
    case EJobTitle.CLASSIFICATION_SCAN:
    case ETaskName.CLASSIFICATION_SCAN: {
      if (result === EJobResultType.OK) {
        return { color: 'success.main', label: 'Classification scan' };
      }
      return { color: 'error.main', label: 'Classification scan' };
    }
  }
};

export const EventStatus: FC<EventStatusProps> = ({ event, reverseOrder }) => {
  const statusVariant = getVariant(event);

  return (
    <Box
      sx={{
        display: 'flex',
        gridGap: '1em',
        flexDirection: reverseOrder ? 'row-reverse' : 'row',
      }}
    >
      <Status color={statusVariant.color} label={statusVariant.label} />
      {'flag' in event && event.flag === FlagType.Incorrect && <FlagIcon color="error" />}
    </Box>
  );
};

EventStatus.propTypes = {
  event: Proptypes.any.isRequired,
  reverseOrder: Proptypes.bool,
};
